import { fetch } from '../../utils';
import Backend from '../../backend';
import { handleJobResponse } from '../../utils/job';

export const openaiGenerateWithTextPolling = async (promptIdentifier, promptData, existingMessages) => {
  const response = await handleJobResponse(
    fetch(Backend.methodUrl('openaiGenerateWithTextPolling'), {
      method: 'POST',
      body: JSON.stringify({
        promptIdentifier,
        promptData,
        existingMessages
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    }),
    {
      pollTimeout: 7000
    }
  );

  if (response.error) {
    throw new Error(response.error);
  }

  const { completed, failed } = response;

  const completedTask = completed?.[0];
  const failedTask = failed?.[0];

  if (failedTask) {
    throw new Error(failed.message);
  }

  return completedTask?.extras;
};

export const openaiGenerateWithTextStreaming = async (
  promptIdentifier,
  promptData,
  existingMessages,
  setText = null,
  completeAction = null,
  setLoading = null
) => {
  try {
    for (let data in promptData) {
      if (!promptData[data]) {
        promptData[data] = '';
      }
    }
    await fetch(Backend.methodUrl('openaiGenerateWithTextStreaming'), {
      method: 'POST',
      body: JSON.stringify({
        promptIdentifier,
        promptData,
        existingMessages
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let responseText = '';

        function read() {
          reader.read().then(({ done, value }) => {
            if (done) {
              if (completeAction) {
                completeAction(responseText);
              }
              if (setLoading) {
                setLoading(false);
              }
              return;
            }
            const text = decoder.decode(value, { stream: true });
            responseText += text;
            if (setText) {
              setText(responseText);
            }
            read();
          });
        }

        read();
      })
      .catch(error => {
        console.error('Error streaming data:', error);
        throw error;
      });
  } catch (error) {
    throw error;
  }
};

/**
 * Stream a prompt through the provider-agnostic AI endpoint and resolve with
 * the complete text. Meeting-note sync uses this instead of creating a second
 * transport for a single prompt.
 */
export const aiGenerateWithTextStreaming = async (promptIdentifier, promptData, { timeoutMs = 30000 } = {}) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  try {
    const response = await fetch(Backend.methodUrl(`ai/${encodeURIComponent(promptIdentifier)}`), {
      method: 'POST',
      body: JSON.stringify({ promptData, mode: 'streaming' }),
      headers: { 'Content-Type': 'application/json' },
      ...(controller ? { signal: controller.signal } : {})
    });

    if (!response.ok) {
      throw new Error(`AI request failed with status ${response.status}`);
    }

    if (!response.body) return response.text();

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let responseText = '';
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (result.value) responseText += decoder.decode(result.value, { stream: !done });
    }
    return responseText;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};
